import DocsLayout, { CodeBlock, Callout } from '../../../components/docs/DocsLayout';

export default function DocsApiEndpoints() {
  return (
    <DocsLayout 
      title="API Endpoints" 
      description="Complete API reference"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          The AIBL API is RESTful and returns JSON responses. Base URL: <code className="bg-white/10 px-1.5 py-0.5 rounded">https://api.aibl.dev</code>
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Agents</h2>

        <div className="space-y-6">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-mono">GET</span>
              <code className="text-sm text-white">/v1/agents</code>
            </div>
            <p className="text-sm text-white/60 mb-3">List all agents for the authenticated account.</p>
            <CodeBlock language="json">
{`{
  "success": true,
  "data": {
    "agents": [
      {
        "id": "agent_abc123",
        "name": "TradingBot",
        "status": "active",
        "chain": "base"
      }
    ]
  }
}`}
            </CodeBlock>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs font-mono">POST</span>
              <code className="text-sm text-white">/v1/agents</code>
            </div>
            <p className="text-sm text-white/60 mb-3">Create a new agent.</p>
            <CodeBlock language="json">
{`// Request
{
  "name": "NewAgent",
  "chain": "base"
}

// Response
{
  "success": true,
  "data": {
    "agent": { "id": "agent_xyz789", "name": "NewAgent" },
    "wallet": { "address": "0x...", "chain": "base" },
    "apiKey": "aibl_key_..."
  }
}`}
            </CodeBlock>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-mono">GET</span>
              <code className="text-sm text-white">/v1/agents/:agentId</code>
            </div>
            <p className="text-sm text-white/60">Get details for a specific agent.</p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Plugins</h2>

        <div className="space-y-6">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-mono">GET</span>
              <code className="text-sm text-white">/v1/agents/:agentId/plugins</code>
            </div>
            <p className="text-sm text-white/60">Get all plugin states for an agent.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 text-xs font-mono">PATCH</span>
              <code className="text-sm text-white">/v1/agents/:agentId/plugins/:pluginName</code>
            </div>
            <p className="text-sm text-white/60 mb-3">Update plugin configuration.</p>
            <CodeBlock language="json">
{`{
  "enabled": true,
  "config": {
    "maxSlippage": 1,
    "maxAmount": "1000000000000000000"
  }
}`}
            </CodeBlock>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Wallets</h2>

        <div className="space-y-6">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-mono">GET</span>
              <code className="text-sm text-white">/v1/agents/:agentId/wallets</code>
            </div>
            <p className="text-sm text-white/60">List wallets for an agent.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs font-mono">POST</span>
              <code className="text-sm text-white">/v1/agents/:agentId/wallets</code>
            </div>
            <p className="text-sm text-white/60">Add a new wallet on a different chain.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-mono">GET</span>
              <code className="text-sm text-white">/v1/agents/:agentId/wallets/:walletId/balance</code>
            </div>
            <p className="text-sm text-white/60">Get wallet balance for specific tokens.</p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Gnosis Safe</h2>

        <div className="space-y-6">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs font-mono">POST</span>
              <code className="text-sm text-white">/v1/safe/deploy</code>
            </div>
            <p className="text-sm text-white/60 mb-3">Deploy a new Gnosis Safe.</p>
            <CodeBlock language="json">
{`{
  "agentId": "agent_abc123",
  "signers": ["0x...", "0x..."],
  "threshold": 2
}`}
            </CodeBlock>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs font-mono">POST</span>
              <code className="text-sm text-white">/v1/safe/import</code>
            </div>
            <p className="text-sm text-white/60">Import an existing Safe.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs font-mono">GET</span>
              <code className="text-sm text-white">/v1/safe/:agentId/transactions</code>
            </div>
            <p className="text-sm text-white/60">Get pending and executed transactions.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs font-mono">POST</span>
              <code className="text-sm text-white">/v1/safe/confirm/:safeTxId</code>
            </div>
            <p className="text-sm text-white/60">Approve a pending transaction.</p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs font-mono">POST</span>
              <code className="text-sm text-white">/v1/safe/execute/:safeTxId</code>
            </div>
            <p className="text-sm text-white/60">Execute a fully-signed transaction.</p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Error Responses</h2>
        
        <p className="text-white/70 mb-4">
          All errors follow a consistent format:
        </p>

        <CodeBlock language="json">
{`{
  "success": false,
  "error": {
    "code": "INSUFFICIENT_FUNDS",
    "message": "Agent wallet has insufficient balance for this operation"
  }
}`}
        </CodeBlock>

        <Callout type="info" title="Error Codes">
          See our error code reference for a complete list of possible error codes and their meanings.
        </Callout>
      </section>
    </DocsLayout>
  );
}
